import type { ProductServiceDto, SlotDto } from '@telestore/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { CalendarX2 } from 'lucide-react';
import type { Locale, TranslationKey, TranslationParams } from '@telestore/i18n';
import { api } from '../lib/api.js';
import { Spinner } from './primitives.js';

/**
 * Services slot picker (§4: slot engine = hours − bookings − buffer).
 *
 * The engine runs on the server; this only renders what it returns. That
 * matters more here than anywhere else in the storefront, because two
 * customers looking at the same Thursday must never both be told 3pm is free
 * — so the list is refetched rather than cached, and the real defence is the
 * `FOR UPDATE` re-check `holdSlot` does when the booking is actually made.
 *
 * §6.5: Cambodia is one timezone. Every date here is a plain `YYYY-MM-DD` in
 * Phnom Penh terms with no offset arithmetic anywhere.
 */

export interface SlotPickerProps {
  shopSlug: string;
  productId: string;
  service: ProductServiceDto;
  value: { startsAt: string; resourceId: string } | null;
  onChange: (slot: { startsAt: string; resourceId: string } | null) => void;
  locale: Locale;
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

/** The next 14 days as `YYYY-MM-DD`, starting today. */
function upcomingDates(count: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let index = 0; index < count; index += 1) {
    const day = new Date(today.getTime() + index * 86_400_000);
    out.push(day.toISOString().slice(0, 10));
  }
  return out;
}

const WEEKDAY_KEYS = [
  'common.sun',
  'common.mon',
  'common.tue',
  'common.wed',
  'common.thu',
  'common.fri',
  'common.sat',
] as const;

export function SlotPicker({
  shopSlug,
  productId,
  service,
  value,
  onChange,
  locale,
  t,
}: SlotPickerProps) {
  const dates = useMemo(() => upcomingDates(14), []);
  const [date, setDate] = useState(dates[0]!);
  const [resourceId, setResourceId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['slots', shopSlug, productId, date, resourceId],
    queryFn: () => api.slots(shopSlug, { productId, date, ...(resourceId ? { resourceId } : {}) }),
    /**
     * A slot list goes stale the moment somebody else books. Ten seconds is
     * short enough that a customer rarely taps a slot that has just gone, and
     * long enough that scrolling the day strip does not hammer the API.
     */
    staleTime: 10_000,
  });

  const slots = query.data?.slots ?? [];

  function pickDate(next: string) {
    setDate(next);
    // The chosen time belonged to the old day; keeping it would let a
    // customer book Tuesday 3pm while the screen shows Wednesday.
    onChange(null);
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Who — only when the shop has more than one bookable person (§4). */}
      {service.resources.length > 1 ? (
        <div>
          <p className="mb-2 text-sm font-medium">{t('storefront.chooseStaff')}</p>
          <div className="flex flex-wrap gap-2">
            <Chip
              label={t('storefront.anyStaff')}
              active={resourceId === null}
              onClick={() => {
                setResourceId(null);
                onChange(null);
              }}
            />
            {service.resources.map((resource) => (
              <Chip
                key={resource.id}
                label={resource.name}
                active={resourceId === resource.id}
                onClick={() => {
                  setResourceId(resource.id);
                  onChange(null);
                }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* When — a horizontal strip of days, the way a booking app does it. */}
      <div>
        <p className="mb-2 text-sm font-medium">{t('storefront.chooseDate')}</p>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {dates.map((entry) => {
            const day = new Date(`${entry}T00:00:00`);
            const active = entry === date;
            return (
              <button
                key={entry}
                type="button"
                onClick={() => pickDate(entry)}
                className="flex w-14 shrink-0 flex-col items-center gap-0.5 rounded-[var(--radius)] py-2"
                style={{
                  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: active ? 'var(--color-primary)' : 'transparent',
                  color: active ? 'var(--color-primary-contrast)' : 'var(--color-text)',
                }}
              >
                <span className="text-[11px] opacity-70">
                  {t(WEEKDAY_KEYS[day.getDay()]!)}
                </span>
                <span className="text-base font-semibold tabular-nums">{day.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* The slots themselves. */}
      <div>
        <p className="mb-2 text-sm font-medium">{t('storefront.chooseTime')}</p>

        {query.isPending ? <Spinner label={t('common.loading')} /> : null}

        {!query.isPending && slots.length === 0 ? (
          <div
            className="flex items-center gap-2 rounded-[var(--radius-card)] px-3 py-4 text-sm opacity-70"
            style={{ background: 'var(--color-surface)' }}
          >
            <CalendarX2 size={16} aria-hidden="true" />
            {/*
              §4: the API says WHY a day is empty, so the customer knows
              whether to try another day or another shop.
            */}
            {t(
              query.data?.closedReason === 'no_hours'
                ? 'storefront.slotsClosed'
                : query.data?.closedReason === 'past'
                  ? 'storefront.slotsPast'
                  : 'storefront.slotsFull',
            )}
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {slots.map((slot: SlotDto) => {
            const active = value?.startsAt === slot.startsAt && value.resourceId === slot.resourceId;
            return (
              <button
                key={`${slot.resourceId}:${slot.startsAt}`}
                type="button"
                onClick={() => onChange({ startsAt: slot.startsAt, resourceId: slot.resourceId })}
                className="rounded-[var(--radius)] px-2 py-2.5 text-sm font-medium tabular-nums"
                style={{
                  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: active ? 'var(--color-primary)' : 'transparent',
                  color: active ? 'var(--color-primary-contrast)' : 'var(--color-text)',
                }}
              >
                {clockOf(slot.startsAt)}
                {/*
                  Whose slot it is, but only when the customer let us choose —
                  having asked for Sophea, they do not need to be told Sophea
                  four times over.
                */}
                {resourceId === null && service.resources.length > 1 ? (
                  <span className="block text-[10px] font-normal opacity-60">
                    {slot.resourceName}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* §4: a deposit holds the slot; without one it is booked on trust. */}
      {service.depositAmount !== null && service.depositAmount > 0 ? (
        <p className="text-xs opacity-70">{t('storefront.depositNotice')}</p>
      ) : null}
    </div>
  );
}

/**
 * `HH:MM` in Phnom Penh from an ISO instant.
 *
 * §6.5 again: the offset is fixed at +7 and never observes DST, so this is
 * arithmetic rather than a reason to ship a timezone library to a phone.
 */
function clockOf(iso: string): string {
  const shifted = new Date(new Date(iso).getTime() + 7 * 3_600_000);
  return shifted.toISOString().slice(11, 16);
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-1.5 text-sm"
      style={{
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
        background: active ? 'var(--color-primary)' : 'transparent',
        color: active ? 'var(--color-primary-contrast)' : 'var(--color-text)',
      }}
    >
      {label}
    </button>
  );
}
